/**
 * This module handles all validation functionality with redux-form-validations,
 * including any custom validators
 */
import Validators, { addValidator, numericality } from 'redux-form-validators'
import _ from 'lodash'
import { constants, utils } from '@adc/ui-core'
import { parseJSONData } from './json'
import { isAnyAddressFieldSet } from './address'

const { currency } = utils

/**
 * Default overrides - general settings
 */
Validators.defaultOptions = {
  ...Validators.defaultOptions,
  caseSensitive: false,
}

/**
 * Custom validation messages (to override messages from out-of-the-box validators)
 */
Validators.messages = {
  ...Validators.messages,
  presence: 'Required',
  tooShort: '{count, number} Character Minimum',
  tooLong: '{count, number} Character Maximum',
}

/**
 * Custom validators
 */
export const doesNotIncludeHttp = addValidator({
  defaultMessage: 'Do not include "http://"',
  validator: function (_, value) {
    return /^(?!(http:\/\/|https:\/\/)).*$/g.test(value)
  },
})

export const validUrlWithoutProtocol = addValidator({
  defaultMessage: 'Invalid URL',
  validator: function (options, value) {
    return (
      (options.allowBlank && !value) ||
      /^[-a-zA-Z0-9@:%_+.~#?&/=]{2,256}\.[a-z]{2,4}((\/|\?)[-a-zA-Z0-9@:%_+.~#?&/=]*)?$/gi.test(
        value
      )
    )
  },
})

export const validUrl = addValidator({
  defaultMessage: 'Invalid URL',
  validator: function (options, value) {
    return (
      (options.allowBlank && !value) ||
      /^(http:\/\/|https:\/\/)[-a-zA-Z0-9@:%_+.~#?&;/=]{2,256}\.[a-z]{2,4}((\/|\?)[-a-zA-Z0-9@:%_+,.~#?&;/=]*)?$/gi.test(
        value
      )
    )
  },
})

/**
 * Verify time is in HH:MM AM format, and not something like 16:45 AM
 */
export const validTime = addValidator({
  defaultMessage: 'Invalid time specified',
  validator: function (options, value) {
    return (
      (options.allowBlank && !value) ||
      /^((1[0-2]|0[1-9]):([0-5][0-9]) ([AaPp][Mm]))$/g.test(value)
    )
  },
})

/**
 * Require address to be submitted with a full set of pre-defined address values
 * Additional required fields in addition to street name and number are defined via the addressFields option
 */
export const requiredAddressPart = addValidator({
  defaultMessage: 'Required',
  validator: function (options, value, allValues) {
    const addressPath = options.addressPath
    const address = _.get(allValues, addressPath, {})
    // short circuit if all address fields are empty
    if (
      options.allowNone &&
      !isAnyAddressFieldSet(address, options.addressFields)
    )
      return true
    // special handling for the address suggestion field since it handles two inputs
    if (options.isAddressSuggestion) {
      const { street_name: streetName } = value
      return !_.isEmpty(streetName)
    }
    // default case
    return !_.isEmpty(value)
  },
})

export const atLeastOneRequired = addValidator({
  defaultMessage: 'Please fill at least one field',
  validator: function (options, value, values) {
    const keys = _.keys(_.pick(values, options.fieldsToCheck))
    const validFields = _.filter(keys, key => {
      // _.trim converts boolean to string
      return _.trim(values[key]).length
    })
    return validFields.length !== 0
  },
})

export const requiredFile = addValidator({
  defaultMessage: 'Required',
  validator: function (options, value) {
    return value !== ''
  },
})

export const requiredMultipleSelect = addValidator({
  defaultMessage: 'Required',
  validator: function (options, value) {
    return !_.isEmpty(value)
  },
})

const { MODES } = constants.optionLists
export const requiredMultipleSelectWithMode = addValidator({
  defaultMessage: 'You must select a value',
  validator: function (options, value) {
    const parsed = parseJSONData(value)
    const mode = _.get(parsed, 'mode', MODES.ALL)
    return (
      mode === MODES.ALL ||
      ([MODES.INCLUDE, MODES.EXCLUDE].includes(mode) &&
        !_.isEmpty(_.get(parsed, 'value')))
    )
  },
})

/**
 * Numericality validation for our currency input fields where the value is a string
 * Out of the box numericality validator checks against number type
 */
export const numericalityString = addValidator({
  defaultMessage: 'Invalid value',
  validator: function (options, value) {
    // numericality returns an error msg if it exists, otherwise it returns undefined
    return !numericality(options)(currency.parse(value))
  },
})

/**
 * Export (pass through) all default validators from redux-form-validators
 */
export { Validators }
export * from 'redux-form-validators'
